/**
 * An affiliate's earnings (TMMRSL-0051, MM-D-106): `GET /tenants/{slug}/affiliates/{id}/earnings`
 * answers `{ currency, months }`, newest first, each month an amount per status (TMMAPI-0104:
 * recurring, pending, payable, voided) and per kind (TMMAPI-0105 adds bounty, milestone, clawed
 * back and recovered). Every amount is required. A route that isn't deployed answers `404` (not
 * `TENANT_NOT_FOUND`), and the section is hidden (`isEarningsRouteMissing`).
 */
import type {
  AffiliateEarnings as AffiliateEarnings_Gen,
  AffiliateEarningsMonth,
} from '@/api/generated';
import { ApiError, backendCode } from '@/api/errors';

export type EarningsColumn =
  | 'recurring'
  | 'bounty'
  | 'milestone'
  | 'pending'
  | 'payable'
  | 'voided'
  | 'clawedBack'
  | 'recovered';

export interface EarningsMonth {
  /** `YYYY-MM`. */
  month: string;
  amounts: Record<EarningsColumn, number>;
}

export interface AffiliateEarnings {
  currency: string;
  /** The server's order (newest first). */
  months: EarningsMonth[];
  /** Every amount column. Each wire key is required on a month. */
  present: ReadonlySet<EarningsColumn>;
}

type AmountKey = {
  [K in keyof AffiliateEarningsMonth]: AffiliateEarningsMonth[K] extends number ? K : never;
}[keyof AffiliateEarningsMonth];

/** Each column's wire key on a month. */
const WIRE_KEYS: Record<EarningsColumn, AmountKey> = {
  recurring: 'recurringMinor',
  bounty: 'bountyMinor',
  milestone: 'milestoneMinor',
  pending: 'pendingMinor',
  payable: 'payableMinor',
  voided: 'voidedMinor',
  clawedBack: 'clawedBackMinor',
  recovered: 'recoveredMinor',
};

const COLUMNS = Object.keys(WIRE_KEYS) as EarningsColumn[];

/** The earnings body. Every column is present; each amount is the number tmm-api sent. */
export function normalizeAffiliateEarnings(body: unknown): AffiliateEarnings {
  const raw = body as AffiliateEarnings_Gen;
  const present = new Set<EarningsColumn>(COLUMNS);
  return {
    currency: raw.currency,
    months: raw.months.map((month) => {
      const amounts = {} as EarningsMonth['amounts'];
      for (const column of COLUMNS) {
        const key = WIRE_KEYS[column];
        amounts[column] = month[key];
      }
      return { month: month.month, amounts };
    }),
    present,
  };
}

/**
 * Defensively hide a missing earnings route: a `404` whose backend code isn't `TENANT_NOT_FOUND` (that
 * one is the tenant workspace's, which leaves the tenant).
 */
export function isEarningsRouteMissing(err: unknown): boolean {
  return (
    err instanceof ApiError && err.code === 'NOT_FOUND' && backendCode(err) !== 'TENANT_NOT_FOUND'
  );
}
